import IconCatch, { findIcon } from '@/components/icons/icon-catch/icon-catch';
import { Minus } from '@/components/icons/minus';
import { Plus } from '@/components/icons/plus';
import { IconArrowRight } from '@cmpsr/components';
import { Link as ReactRouterLink } from 'react-router-dom';

type RiskFactorsBreakdownCardProps = {
  id: string;
  iconId: string;
  title: string;
  subtitle?: string;
  isExpanded: boolean;
  toggleExpanded: () => void;
  onOpenRiskFactorModal?: () => void;
  headerPills?: {
    subtitle: string;
    value: string;
    testId?: string;
    // Either inline styles OR Tailwind className
    color?: string;
    textColor?: string;
    className?: string;
  }[];
  hideChildren?: boolean;
  link?: string;
  average?: 'above' | 'below' | 'average';
  risk?: number | string;
  hideRiskBadge?: boolean;
};


export const RiskFactorsBreakdownCard = ({
  id,
  iconId,
  title,
  subtitle,
  isExpanded,
  toggleExpanded,
  headerPills = [],
  children,
  hideChildren = false,
  link,
  onOpenRiskFactorModal,
}: RiskFactorsBreakdownCardProps & React.PropsWithChildren) => {

  const Header = () => {
    return (
      <div data-anchor-id={id} onClick={toggleExpanded} className="dashboard-reset cursor-pointer">
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row gap-3 items-center">
            <div className="w-[60px] h-[60px] bg-white border border-[var(--color-catch-cream)] flex items-center justify-center rounded-[6px] relative">
              <IconCatch
                icon={findIcon(iconId) ?? 'biomarkers'}
                alt={title}
                width="45px"
                height="45px"
              />
            </div>
            {link && (
              <ReactRouterLink
                to={link}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="underline hover:no-underline text-[var(--color-catch-dark-blue-90)] flex items-center gap-1"
              >
                <span
                  className="text-[25px] font-normal text-[var(--color-catch-dark-blue-90)] capitalize leading-[1.3]"
                >
                  {title}
                </span>
                <IconArrowRight />
              </ReactRouterLink>
            )}
            {onOpenRiskFactorModal && (
              <span
                className="text-[25px] font-normal text-[var(--color-catch-dark-blue-90)] capitalize underline hover:no-underline cursor-pointer leading-[1.3] flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenRiskFactorModal();
                }}
              >
                {title}
                <IconArrowRight />
              </span>
            )}
            {!link && !onOpenRiskFactorModal && (
              <span className="text-[25px] font-normal text-[var(--color-catch-dark-blue-90)] capitalize leading-[1.3]">
                {title}
              </span>
            )}
          </div>

          <div className="flex flex-row gap-4 items-center">
            <button
              data-testid={`risk-factors-breakdown-card-expand-toggle-${id}`}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              className="bg-transparent hover:bg-transparent active:bg-transparent text-[var(--color-catch-dark-blue-90)] p-2"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded();
              }}
            >
              {isExpanded ? (
                <Minus/>
              ) : (
                <Plus />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const Pills = () => {
    const pills = headerPills.slice(0, 3);

    return (
      <div className="flex flex-row gap-[25px] w-full">
        {pills.map(({ subtitle, color, value, textColor, testId, className }) => (
          <div className="flex flex-col gap-[10px] flex-1" key={subtitle}>
            <div
              className={` ${className} rounded-[4px] flex items-center justify-center px-[15px] py-2.5`}
              style={className ? {} : { backgroundColor: color }}
            >
              <span
                className="text-[20px] font-medium leading-[1.3] text-center"
                style={className ? {} : { color: textColor }}
                data-testid={testId}
              >
                {value}
              </span>
            </div>
            <span className="text-[14px] font-normal text-[var(--color-catch-dark-blue-90)] leading-[1.3]">
              {subtitle}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`bg-[#f0f5f6] p-[25px] pb-8 rounded-med flex flex-col ${isExpanded ? 'gap-5' : 'gap-0'}`}
      data-testid={`risk-factors-breakdown-card-${id}_${isExpanded ? 'expanded' : 'collapsed'}`}
    >
      <Header />
      {isExpanded && (
        <>
          {subtitle && (
            <p className="text-sm font-normal text-[var(--color-catch-dark-blue-90)] pl-[75px] pr-[15px] mb-1">
              {subtitle}
            </p>
          )}
          <Pills />
          {!hideChildren && (
            <>
              <hr className="opacity-100" />
              <div>{children}</div>
            </>
          )}
        </>
      )}
    </div>
  );
};
